import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from './stores/auth.js';
import { useFeatureFlagsStore } from './stores/featureFlags.js';

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition;
        else return { top: 0, behavior: 'smooth' };
    },
    routes: [
        {
            path: '/',
            name: 'home',
            component: () => import('./views/Home.vue'),
            meta: { auth: false, both: true },
        },
        // Redirect root to dashboard (can be changed later)
        // {
        //     path: '/',
        //     redirect: '/dashboard',
        // },
        {
            path: '/about',
            name: 'about',
            component: () => import('./views/About.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/contact',
            name: 'contact',
            component: () => import('./views/Contact.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('./views/Login.vue'),
            meta: { auth: false, both: false },
        },
        {
            path: '/signup',
            name: 'signup',
            redirect: '/login',
        },
        {
            path: '/projects',
            name: 'projects',
            component: () => import('./views/Projects.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/feed',
            name: 'feed',
            component: () => import('./views/Feed.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/qna',
            name: 'qna',
            component: () => import('./views/Qna.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/qna/ask',
            name: 'qna-new',
            component: () => import('./views/QuestionAsk.vue'),
            meta: { auth: true },
        },
        {
            path: '/qna/ask/:slug',
            name: 'qna-edit',
            component: () => import('./views/EditAskedQuestion.vue'),
            meta: { auth: true },
        },
        {
            path: '/qna/:slug',
            name: 'qna-detail',
            component: () => import('./views/QuestionDetail.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/projects/:slug',
            name: 'project-detail',
            component: () => import('./views/ProjectDetail.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/projects/:slug/edit',
            name: 'edit-project',
            component: () => import('./views/EditProject.vue'),
            meta: { auth: true },
        },
        {
            path: '/projects/create',
            name: 'add-project',
            component: () => import('./views/AddProject.vue'),
            meta: { auth: true },
        },
        {
            path: '/dashboard',
            name: 'profile',
            component: () => import('./views/Profile.vue'),
            meta: { auth: true, both: false },
        },
        {
            path: '/profile/update',
            name: 'profile-update',
            component: () => import('./views/UpdateProfile.vue'),
            meta: { auth: true, both: false },
        },
        {
            path: '/@:username',
            name: 'user-feed',
            component: () => import('./views/UserProfile.vue'),
            meta: {auth: false, both: true},
        },
        {
            path: '/feed/post_:post_code',
            name: 'post-detail',
            component: () => import('./views/PostDetail.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/search',
            name: 'search-results',
            component: () => import('./views/SearchResults.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/notifications',
            name: 'notifications',
            component: () => import('./views/Notifications.vue'),
            meta: { auth: true },
        },
        {
            path: '/settings',
            name: 'settings',
            component: () => import('./views/Settings.vue'),
            meta: { auth: true },
        },
        {
            path: '/profile/:id?',
            name: 'profile-view',
            component: () => import('./views/ProfileView.vue'),
            props: true,
            meta: { auth: true, both: false },
        },
        {
            path: '/questions',
            name: 'questions.index',
            component: () => import('./views/QuestionsView.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/ask-question',
            name: 'questions.ask',
            component: () => import('./views/AskQuestionView.vue'),
            meta: { auth: true, both: false },
        },
        {
            path: '/questions/:id',
            name: 'questions.show',
            component: () => import('./views/SingleQuestionView.vue'),
            props: true,
            meta: { auth: false, both: true },
        },
        {
            path: '/badges',
            name: 'badges.index',
            component: () => import('./views/BadgesView.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/tasks',
            name: 'tasks.index',
            component: () => import('./views/TasksView.vue'),
            meta: { auth: true, both: false },
        },
        {
            path: '/levels',
            name: 'levels.index',
            component: () => import('./views/LevelsView.vue'),
            meta: { auth: false, both: true },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('./views/ErrorPage.vue'),
            meta: { auth: false, both: true, },
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const featureFlags = useFeatureFlagsStore();

    const isQnaRoute = to.path.startsWith('/qna') || to.path.startsWith('/questions') || to.path.startsWith('/ask-question');
    if (isQnaRoute) {
        if (!featureFlags.loaded) {
            await featureFlags.fetchFlags();
        }
        if (!featureFlags.isAiQnaEnabled) {
            return next('/');
        }
    }

    const isLogged = () => authStore?.isAuthenticated;

    if (to.matched.some(record => record.meta.auth)) {
        if (isLogged()) {
            // Auto-complete tasks based on route (non-blocking)
            if (to.name) {
                import('./composables/useAutoTaskCompletion.js').then(({ useAutoTaskCompletion }) => {
                    const { completeTaskByRoute } = useAutoTaskCompletion();
                    completeTaskByRoute(to.name).catch(() => {});
                }).catch(() => {});
            }
            next();
        } else {
            if (to.name !== 'login') next({ name: 'login' });
            else next();
        }
    } else if (to.matched.some(record => record.meta.both)) {
        next();
    } else {
        if (isLogged()) next('/');
        else {
            if (to.name !== 'login') next({ name: 'login' });
            else next();
        }
    }
});

export default router;
